import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement[]} [dots] - Optional mobile line-nav segments above thumbnails.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #galleryScroller = null;

  /** @type {number} */
  #selectedIndex = 0;

  /** @type {boolean} */
  #live = false;

  /**
   * Bind scroll listeners on first open so a closed dialog is free on PDP.
   */
  #bindLive() {
    if (this.#live) return;

    this.#live = true;
    this.#galleryScroller = this.querySelector('[data-zoom-gallery]');
    this.#galleryScroller?.addEventListener('scroll', this.handleScroll);
    this.#galleryScroller?.addEventListener('wheel', this.#handleGalleryWheel, { passive: false });
    this.addEventListener('zoom-state-change', this.#handleZoomStateChange);
  }

  #activateWrappers() {
    for (const media of this.refs.media || []) {
      if (!(media instanceof HTMLElement)) continue;
      const wrapper = media.querySelector('[data-zoom-wrapper]');
      if (typeof wrapper?.activate === 'function') wrapper.activate();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#galleryScroller?.removeEventListener('scroll', this.handleScroll);
    this.#galleryScroller?.removeEventListener('wheel', this.#handleGalleryWheel);
    this.#galleryScroller = null;
    this.removeEventListener('zoom-state-change', this.#handleZoomStateChange);
    // Listeners are gone, so the next open() has to bind them again.
    this.#live = false;
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog, media } = this.refs;
    const targetImage = media[index];

    // selectThumbnail() already resets the wrappers, scrolls the gallery and syncs the chrome.
    const open = () => {
      this.#bindLive();
      this.#activateWrappers();
      dialog.showModal();
      this.selectThumbnail(index, { behavior: 'instant' });
    };

    /**
     * `[data-zoom-source]` lets a gallery nominate its own morph source when its media are not
     * `li` / `slideshow-slide` (the custom-bundle grid uses `figure` / `div`). It is listed first
     * for readability only — `closest` walks up and tests the whole list per level, so the nearest
     * ancestor wins. That is what makes a tapped tile beat its enclosing slide on mobile.
     * @type {HTMLElement | null}
     */
    const sourceImage =
      event.target instanceof Element ? event.target.closest('[data-zoom-source],li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    // Open and close are separate types because the sticky header needs opposite treatment in each,
    // and a type is the only handle CSS gets on a transition. Neither carries setup of its own;
    // `sections/header.liquid` owns both rules. On open the page is hidden behind the dialog's
    // backdrop, so the header must stay out of the transition layer or it floats above that cover.
    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    }, ['zoom-dialog-open']);

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');
  }

  /**
   * Loads a high-resolution image for a specific media container.
   * Resolves after the high-res image is decoded (or immediately if already swapped).
   *
   * Every caller treats this as fire-and-forget, so it never rejects: a failed load or a
   * failed swap resolves to `false` instead. Keep it that way — callers do not attach
   * rejection handlers.
   *
   * @param {HTMLElement} mediaContainer - The media container element
   * @returns {Promise<boolean>}
   */
  async loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer?.querySelector?.('[data-zoom-wrapper]')) return false;

    const image = mediaContainer.querySelector('[data-zoom-wrapper] img');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl) return false;

    if (this.#highResImagesLoaded.has(highResolutionUrl) || image.currentSrc === highResolutionUrl || image.src === highResolutionUrl) {
      this.#highResImagesLoaded.add(highResolutionUrl);
      return true;
    }

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');
    if (image.sizes) newImage.sizes = image.sizes;
    newImage.src = highResolutionUrl;

    let loaded = true;

    try {
      await newImage.decode();
    } catch {
      loaded = await new Promise((resolve) => {
        // decode() also rejects for reasons other than a failed fetch, so re-check the element.
        if (newImage.complete) return resolve(newImage.naturalWidth > 0);
        newImage.onload = () => resolve(true);
        newImage.onerror = () => resolve(false);
      });
    }

    // Never swap a working image for a broken one.
    if (!loaded) return false;

    if (image.isConnected) {
      image.replaceWith(newImage);
      const wrapper = mediaContainer.querySelector('[data-zoom-wrapper]');
      if (typeof wrapper?.updatedCallback === 'function') {
        try {
          wrapper.updatedCallback();
        } catch (error) {
          // The swap already succeeded; a wrapper that fails to re-measure must not reject.
          console.warn('Zoom: re-measuring the wrapper after the high-res swap failed', error);
        }
      }
    }

    this.#highResImagesLoaded.add(highResolutionUrl);
    return true;
  }

  /**
   * Convert vertical mouse-wheel to horizontal slide movement at fit.
   * Native trackpad horizontal pan is left alone.
   * @param {WheelEvent} event
   */
  #handleGalleryWheel = (event) => {
    if (this.dataset.zoomState === 'detail') return;
    const scroller = this.#galleryScroller;
    if (!scroller) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    event.preventDefault();
    scroller.scrollLeft += event.deltaY;
  };

  /**
   * Handles the scroll event of the gallery track, used to update the active thumbnail when the corresponding image is visible.
   */
  handleScroll = debounce(async () => {
    const { media } = this.refs;
    if (!media?.length) return;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    if (activeIndex < 0) return;

    // Read before writing, so the attribute updates below cannot force a synchronous layout.
    const thumbButtons = this.#thumbnailButtons();
    const thumbScroll = this.#measureThumbnailScroll(thumbButtons[activeIndex]);

    if (activeIndex !== this.#selectedIndex) {
      this.#resetAllWrappers();
    }

    this.#syncSelectedIndex(activeIndex, thumbButtons);
    this.#applyThumbnailScroll(thumbScroll);
    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Keep thumbnails + optional mobile line-nav in sync with the active slide.
   * Write-only: callers measure the thumbnail scroll first, then apply it.
   * @param {number} index
   * @param {HTMLElement[]} [thumbButtons]
   */
  #syncSelectedIndex(index, thumbButtons) {
    const { dots } = this.refs;
    this.#selectedIndex = index;
    this.#syncChrome(index);

    for (const [i, button] of (thumbButtons ?? this.#thumbnailButtons()).entries()) {
      button.setAttribute('aria-selected', `${i === index}`);
    }

    if (!dots?.length) return;

    for (const [i, dot] of dots.entries()) {
      if (!(dot instanceof HTMLElement)) continue;
      dot.setAttribute('aria-selected', `${i === index}`);
    }
  }

  /**
   * @returns {HTMLElement[]}
   */
  #thumbnailButtons() {
    const { thumbnails } = this.refs;
    return thumbnails instanceof HTMLElement ? [...thumbnails.querySelectorAll('button')] : [];
  }

  /**
   * Where the thumbnail rail has to scroll to keep the active thumbnail in view.
   * Pure layout read — run it before any attribute write to avoid a forced reflow.
   *
   * @param {HTMLElement | undefined} target
   * @returns {{ top: number, left: number } | null} Null when no scrolling is needed.
   */
  #measureThumbnailScroll(target) {
    const { thumbnails } = this.refs;
    if (!(thumbnails instanceof HTMLElement)) return null;
    if (!(target instanceof HTMLElement)) return null;

    const scrollsY = thumbnails.scrollHeight > thumbnails.clientHeight;
    const scrollsX = thumbnails.scrollWidth > thumbnails.clientWidth;
    if (!scrollsY && !scrollsX) return null;

    const item = target.getBoundingClientRect();
    const rail = thumbnails.getBoundingClientRect();

    /**
     * Minimal move that brings the item fully inside the rail.
     * @param {number} itemStart - Item edge, viewport-relative
     * @param {number} itemSize - Item length along the scroll axis
     * @param {number} railStart - Rail edge, viewport-relative
     * @param {number} railSize - Visible rail length along the scroll axis
     * @param {number} current - Current scroll offset on that axis
     * @returns {number} Scroll offset that reveals the item, unchanged when already visible
     */
    const nearest = (itemStart, itemSize, railStart, railSize, current) => {
      const overshootStart = itemStart - railStart;
      const overshootEnd = overshootStart + itemSize - railSize;
      if (overshootStart < 0) return current + overshootStart;
      if (overshootEnd > 0) return current + overshootEnd;
      return current;
    };

    const top = scrollsY
      ? nearest(item.top, item.height, rail.top, thumbnails.clientHeight, thumbnails.scrollTop)
      : thumbnails.scrollTop;

    let left = thumbnails.scrollLeft;
    if (scrollsX) {
      // Mobile centers the active thumbnail in the horizontal strip.
      left = window.matchMedia('(min-width: 750px)').matches
        ? nearest(item.left, item.width, rail.left, thumbnails.clientWidth, thumbnails.scrollLeft)
        : thumbnails.scrollLeft + item.left - rail.left - thumbnails.clientWidth / 2 + item.width / 2;
    }

    if (Math.abs(top - thumbnails.scrollTop) < 1 && Math.abs(left - thumbnails.scrollLeft) < 1) return null;

    return { top, left };
  }

  /**
   * @param {{ top: number, left: number } | null} position
   * @param {ScrollBehavior} [behavior]
   */
  #applyThumbnailScroll(position, behavior) {
    const { thumbnails } = this.refs;
    if (!position || !(thumbnails instanceof HTMLElement)) return;

    thumbnails.scrollTo({
      top: position.top,
      left: position.left,
      behavior: behavior ?? (prefersReducedMotion() ? 'instant' : 'smooth'),
    });
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    /** @type {HTMLElement | null | undefined} */
    let slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    // A gallery with no `media-gallery` host (custom bundle) nominates its own return element.
    // Counterpart to `[data-zoom-source]` on open: the dialog owns the morph, the gallery owns the
    // index→element mapping, since only it knows how its flat media list maps back to its tiles.
    if (!slide) {
      /** @type {{ item: HTMLElement, index: number, target: HTMLElement | null }} */
      const detail = { item: mostVisibleElement, index: activeIndex, target: null };
      this.dispatchEvent(new CustomEvent('zoom-dialog:return-target', { detail }));
      if (detail.target instanceof HTMLElement) slide = detail.target;
    }

    if (!slide) return this.closeDialog();
    const returnTarget = slide;
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    // Closing drops the backdrop immediately, so the page is on show for the whole flight and the
    // returning image has to pass under the sticky header rather than over it. See the open call.
    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      returnTarget.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    }, ['zoom-dialog-close']);

    returnTarget.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.handleZoomPrev();
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.handleZoomNext();
      return;
    }

    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Zoom-in / zoom-out button. The high-res image loads in the background so a slow
   * connection never delays the toggle; the browser swaps it in once decoded.
   */
  handleZoomToggle() {
    const wrapper = this.#wrapperAt(this.#selectedIndex);
    if (!wrapper || typeof wrapper.toggleZoom !== 'function') return;

    if (wrapper.getState?.() === 'fit') {
      const media = this.refs.media[this.#selectedIndex];
      if (media) this.loadHighResolutionImage(media);
    }

    wrapper.toggleZoom();
  }

  handleZoomPrev() {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(this.#selectedIndex - 1, { behavior });
  }

  handleZoomNext() {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(this.#selectedIndex + 1, { behavior });
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const { media } = this.refs;
    const count = Array.isArray(media) ? media.length : 0;

    if (isNaN(index) || index < 0 || index >= count) return;

    // Read before writing, so the writes below cannot force a synchronous layout.
    const thumbButtons = this.#thumbnailButtons();
    const thumbScroll = this.#measureThumbnailScroll(thumbButtons[index]);

    this.#resetAllWrappers();
    this.#syncSelectedIndex(index, thumbButtons);
    this.#applyThumbnailScroll(thumbScroll, options.behavior);

    const targetImage = media[index];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
        block: 'nearest',
        inline: 'start',
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  /**
   * @param {Event} event
   */
  #handleZoomStateChange = (event) => {
    const wrapper = event.target;
    if (!(wrapper instanceof HTMLElement)) return;
    if (!wrapper.hasAttribute('data-zoom-wrapper')) return;

    const media = wrapper.closest('li');
    const mediaList = this.refs.media;
    if (!media || !Array.isArray(mediaList)) return;
    if (mediaList.indexOf(media) !== this.#selectedIndex) return;

    const state = event instanceof CustomEvent ? event.detail?.state : wrapper.getState?.();
    const next = state === 'detail' ? 'detail' : 'fit';
    this.dataset.zoomState = next;
    this.#syncToggleLabels(next);
  };

  /**
   * @param {number} index
   */
  #syncChrome(index) {
    const mediaList = this.refs.media;
    const count = Array.isArray(mediaList) ? mediaList.length : 0;
    this.dataset.zoomCount = String(count);

    const wrapper = this.#wrapperAt(index);
    const state = wrapper?.getState?.() === 'detail' ? 'detail' : 'fit';
    this.dataset.zoomState = state;
    this.#syncToggleLabels(state);
    this.#syncNavDisabled(index, count);
    this.#syncMediaKind(index);
    this.#syncActiveRatio(index);
  }

  /**
   * Publish the active slide's aspect ratio so CSS can size the chrome gutters to the kadr.
   * Reads inline custom properties only — no getComputedStyle, no layout.
   * @param {number} index
   */
  #syncActiveRatio(index) {
    const media = this.refs.media?.[index];
    if (!(media instanceof HTMLElement)) return;

    // The packshot is normalised to the ratio of the first lifestyle image, which Liquid omits
    // when the product has no image after the packshot. Clearing lets CSS fall back to --zoom-kadr-ratio.
    const ratio = media.hasAttribute('data-zoom-packshot')
      ? this.style.getPropertyValue('--zoom-packshot-ratio')
      : media.style.getPropertyValue('--ratio');

    if (ratio.trim()) this.style.setProperty('--zoom-active-ratio', ratio.trim());
    else this.style.removeProperty('--zoom-active-ratio');
  }

  /**
   * @param {'fit' | 'detail'} state
   */
  #syncToggleLabels(state) {
    const toggle = this.querySelector('[data-zoom-toggle]');
    if (!(toggle instanceof HTMLButtonElement)) return;

    const inLabel = toggle.getAttribute('data-zoom-in-label') || '';
    const outLabel = toggle.getAttribute('data-zoom-out-label') || '';
    const isDetail = state === 'detail';
    toggle.setAttribute('aria-pressed', isDetail ? 'true' : 'false');
    toggle.setAttribute('aria-label', isDetail ? outLabel : inLabel);
  }

  /**
   * @param {number} index
   * @param {number} count
   */
  #syncNavDisabled(index, count) {
    const prev = this.querySelector('[data-zoom-prev]');
    const next = this.querySelector('[data-zoom-next]');
    if (!(prev instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;

    if (count < 2) {
      prev.disabled = true;
      next.disabled = true;
      return;
    }

    prev.disabled = index <= 0;
    next.disabled = index >= count - 1;
  }

  /**
   * @param {number} index
   */
  #syncMediaKind(index) {
    const wrapper = this.#wrapperAt(index);
    this.dataset.zoomMedia = wrapper ? 'image' : 'other';
  }

  /**
   * @param {number} index
   * @returns {HTMLElement & { toggleZoom?: Function, resetZoom?: Function, getState?: Function, activate?: Function } | null}
   */
  #wrapperAt(index) {
    const media = this.refs.media?.[index];
    if (!(media instanceof HTMLElement)) return null;
    const wrapper = media.querySelector('[data-zoom-wrapper]');
    return wrapper instanceof HTMLElement ? wrapper : null;
  }

  #resetAllWrappers() {
    for (const media of this.refs.media || []) {
      if (!(media instanceof HTMLElement)) continue;
      const wrapper = media.querySelector('[data-zoom-wrapper]');
      wrapper?.resetZoom?.();
    }

    this.dataset.zoomState = 'fit';
    this.#syncToggleLabels('fit');
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: [0, 0.1, 0.25, 0.5, 0.75, 0.9, 1],
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
